// Library import
import { useEffect, useState } from 'react';
import { Alert, Modal, Form } from 'react-bootstrap';
import Proptypes from 'prop-types';

// File Import
import CrossIcon from '~/images/icon/cross-gray.png';
import ShowIcon from '~/images/icon/eye-on.png';
import HideIcon from '~/images/icon/eye-off.png';
import SuccessIcon from '~/images/icon/success.png';
import { UserApi } from '@/http/user';

// Components Import
import { Loading } from '@/components';

const initialFormData = {
  email: '',
  name: '',
  password: ''
};
export const UserFormModal = ({ prevFormData, show, isEdit, handleClose }) => {
  const [passwordShow, setPasswordShow] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [serverError, setServerError] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);
  const [showSuccessModal, setSuccessModal] = useState(false);
  const [errors, setErrors] = useState(null);

  useEffect(() => {
    if (isEdit) setFormData(prevFormData);
    if (!show) setFormData(initialFormData);
    return () => {
      setServerError([]);
    };
  }, [show]);

  // Handle change
  const handleChange = (event) => {
    event.preventDefault();
    if (event.target.value) {
      let updateError = { ...errors };

      if (event.target.name !== undefined) updateError[event.target.name] = '';

      setErrors(updateError);
    }
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const findFormErrors = () => {
    const { email, password, name } = formData;
    const emailRegex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;

    const newErrors = {};
    let hasError = false;

    if (!email || email === '') {
      newErrors.email = 'メールアドレスが入力されていません';
      hasError = true;
    } else if (email && !emailRegex.test(email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
      hasError = true;
    }

    if (!isEdit) {
      if (!password || password === '') {
        newErrors.password = 'パスワードが入力されていません';
        hasError = true;
      }
    }

    if (!name || name === '') {
      newErrors.name = 'ユーザー名が入力されていません';
      hasError = true;
    }

    return { newErrors, hasError };
  };

  // Create new user
  const handleSubmitUser = async (event) => {
    event.preventDefault();
    const { newErrors, hasError } = findFormErrors();

    if (hasError) {
      setErrors(newErrors);
    } else {
      try {
        setLoading(true);
        setDisableSubmitBtn(true);
        const response = isEdit ? await UserApi.updateUser(formData) : await UserApi.createUser(formData);
        if (response.status === 200) {
          setLoading(false);
          setDisableSubmitBtn(true);
          setSuccessModal(true);
        }
      } catch (error) {
        setLoading(false);
        setDisableSubmitBtn(false);
        let errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          serverError.push(errors[key][0]);
        });
      }
    }
  };

  const handleSuccess = async (event) => {
    event.preventDefault();

    setLoading(true);
    setDisableSubmitBtn(true);

    setTimeout(() => {
      window.open('/admin/users', '_self');
    }, 1000);
  };

  const handlePassword = () => {
    setPasswordShow(!passwordShow);
  };

  return (
    <Modal show={show} onHide={handleClose} centered animation={true} className='modal-wrapper' backdrop='static'>
      {showSuccessModal ? (
        <div className='p-5'>
          <div className='modal-common-content text-center'>
            <img src={SuccessIcon} alt='Success icon' />
            <p className='text my-4'>登録完了</p>

            <div className='action-btn d-flex justify-content-center align-items-center'>
              <button className='button primary' onClick={(event) => handleSuccess(event)}>
                完了
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className='p-5'>
          <h3 className='heading-title'>{isEdit ? 'アカウント編集' : 'アカウント新規作成'}</h3>
          <Form onSubmit={handleSubmitUser}>
            <div className='mb-3'>
              <Form.Label htmlFor='loginEmail' className='form-label'>
                メールアドレス
              </Form.Label>
              <Form.Control
                type='email'
                name='email'
                onChange={(e) => handleChange(e)}
                placeholder='name@mail.com'
                id='loginEmail'
                value={formData?.email}
                disabled={isEdit && prevFormData?.admin === 0}
                isInvalid={errors?.email}
              />

              <Form.Control.Feedback type='invalid'>{errors?.email}</Form.Control.Feedback>
            </div>
            <div className='mb-3'>
              <Form.Label htmlFor='name' className='form-label'>
                ユーザー名
              </Form.Label>
              <Form.Control
                type='text'
                name='name'
                onChange={(e) => handleChange(e)}
                placeholder='name'
                id='name'
                value={formData?.name}
                isInvalid={errors?.name}
              />

              <Form.Control.Feedback type='invalid'>{errors?.name}</Form.Control.Feedback>
            </div>
            <div className='mb-5'>
              <Form.Label htmlFor='loginPassword' className='form-label'>
                パスワード
              </Form.Label>
              <div className='position-relative'>
                <Form.Control
                  type={passwordShow ? 'text' : 'password'}
                  name='password'
                  onChange={(e) => handleChange(e)}
                  placeholder='***********'
                  id='loginPassword'
                  isInvalid={errors?.password}
                />

                <Form.Control.Feedback className='position-absolute' type='invalid'>
                  {errors?.password}
                </Form.Control.Feedback>
                {formData.password && (
                  <div className='password-visibility'>
                    <img onClick={handlePassword} src={passwordShow ? ShowIcon : HideIcon} alt='password' />
                  </div>
                )}
              </div>
            </div>

            <button type='submit' className='button primary' disabled={disableSubmitBtn}>
              登録
            </button>
          </Form>

          {/* Server error */}
          {serverError.map((error, key) => (
            <Alert variant='danger' key={key}>
              {error}
            </Alert>
          ))}

          {/* Loading */}
          {loading && <Loading />}

          <div
            className='close-modal'
            onClick={() => {
              handleClose();
            }}>
            <img src={CrossIcon} alt='Cross icon' />
          </div>
        </div>
      )}
    </Modal>
  );
};

UserFormModal.prototype = {
  show: Proptypes.bool,
  isEdit: Proptypes.bool,
  handleClose: Proptypes.func,
  prevFormData: Proptypes.object
};
